import { ListItem } from './data.types';
import { Projection, SortingProjection } from './projection.types';

export type DcuplQueryTransformOptions = 'lowercase' | 'trim' | 'removeWhitespace';

export const DcuplQueryOperatorsAsArray = [
  'eq',
  'find',
  'gt',
  'gte',
  'lt',
  'lte',
  'typeof',
  'isTruthy',
  'size',
] as const;

export type DcuplQueryOperator = (typeof DcuplQueryOperatorsAsArray)[number];

export type DcuplQueryType = 'and' | 'or';

type DcuplQueryBase = {
  attribute: string;
  queryKey?: any;
  options?: {
    transform?: DcuplQueryTransformOptions[];
    invert?: boolean;
    arrayValueHandling?: 'some' | 'every';
  };
};

type DcuplQueryEq = DcuplQueryBase & {
  operator: 'eq';
  /**
   * You may search for any value but
   * only values of type string and { key:string } may be used for indexed search
   */
  value: any;
};
type DcuplQueryFind = DcuplQueryBase & {
  operator: 'find';
  value: Record<string, unknown> | string;
};

type DcuplQueryNumbers = DcuplQueryBase & {
  operator: 'gt' | 'lt' | 'gte' | 'lte';
  value: string | number | Date;
};

type DcuplQueryTypeOf = DcuplQueryBase & {
  operator: 'typeof';
  value: string;
};

type DcuplQueryIsTruthy = DcuplQueryBase & {
  operator: 'isTruthy';
  value: boolean;
};

type DcuplQuerySize = DcuplQueryBase & {
  operator: 'size';
  value: number;
};

export type DcuplQuery =
  | DcuplQueryEq
  | DcuplQueryNumbers
  | DcuplQueryTypeOf
  | DcuplQueryIsTruthy
  | DcuplQuerySize
  | DcuplQueryFind;

export type DcuplQueryGroup<ChildQuery extends DcuplQuery | DcuplQueryGroup = any> = {
  groupType?: DcuplQueryType;
  groupKey?: string;
  queries: Array<ChildQuery>;
};

type DcuplItemOptions<T> = {
  start?: number;
  count?: number;
  projection?: Projection<T>;
  sort?: SortingProjection<T>;
};

export type DcuplGlobalQueryOptions<T> = {
  modelKey: string;
} & DcuplItemOptions<T> &
  DcuplQueryGroup;

export type DcuplGlobalItemOptions<T> = {
  modelKey: string;
  itemKey: string;
  projection?: Projection<T>;
};

export type DcuplGlobalManyItemOptions<T> = {
  modelKey: string;
  itemKeys: string[];
  projection?: Projection<T>;
};

export type DcuplListQueryOptions<T> = DcuplItemOptions<T> & DcuplQueryGroup;

export type DcuplListItemOptions<T> = DcuplItemOptions<T> & {
  itemKey: string;
};

export type CustomOperatorFn = (query: DcuplQuery, listItem: ListItem) => boolean;
export type CustomOperatorFnMap = Map<string, CustomOperatorFn>;

export type DcuplQueryStatement<T = any> = DcuplItemOptions<T> & {
  modelKey: string;
  groupType?: DcuplQueryType;
  queries: DcuplQuery[] | DcuplQueryGroup<DcuplQuery>[] | DcuplQueryGroup<DcuplQueryGroup<DcuplQuery>>[];
};
